import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, homeForRole } from "@/lib/auth";
import { toast } from "sonner";
import {
  Search, ShoppingCart, Bell, HelpCircle, ChevronDown, Home, FileText,
  ClipboardList, Receipt, Wallet, LineChart, Package, Truck, ArrowUpRight,
  Sparkles, ShieldCheck, Users, Zap,
} from "lucide-react";

/**
 * Coupa-inspired PunchOut demo for the 3 seeded FMCG customers.
 *   /onebuy            → Unilever One Buy   (default)
 *   /onebuy/nestle     → Nestlé BuyDirect   (mimics Ariba/Coupa green palette)
 *   /onebuy/cocacola   → Coca-Cola Supplier Marketplace (Coke red palette)
 *
 * Each variant renders the same layout (top bar, nav strip, catalogue grid,
 * right-side highlighted PunchOut card) with tenant branding + a different
 * signed-in demo user.
 *
 * On PunchOut we stash `propay_punchout_source` in sessionStorage so the app
 * shell can render a small "← Return to <One Buy>" chip inside the top-bar,
 * mirroring the real Coupa PunchIn return trip.
 */

const TENANTS = {
  unilever: {
    key: "unilever",
    displayName: "Unilever",
    portalName: "One Buy",
    portalSuffix: "Powered by Coupa",
    logoLetter: "U",
    primary: "#1F36C7",
    accent: "#00A9E0",
    userEmail: "marketing@unilever.example",
    userInitials: "AD",
    userName: "Ananya Desai",
    userRole: "Marketing · Unilever India",
    spendYtd: "₹18.4 Cr",
    openReqs: "12",
    approved: "48",
    pending: "5",
    catalog: [
      { icon: Package, label: "Office Supplies", vendor: "Staples · Amazon Business", price: "Catalogue" },
      { icon: Truck, label: "Logistics & Freight", vendor: "DHL · Blue Dart", price: "RFQ" },
      { icon: Users, label: "Contingent Workforce", vendor: "Randstad · Adecco", price: "SOW" },
      { icon: FileText, label: "Marketing Agencies", vendor: "WPP · Publicis · Ogilvy", price: "MSA" },
      { icon: LineChart, label: "Media Buying", vendor: "GroupM · Dentsu", price: "Retainer" },
      { icon: Zap, label: "Digital SaaS", vendor: "Salesforce · Adobe · SAP", price: "Subscription" },
    ],
  },
  nestle: {
    key: "nestle",
    displayName: "Nestlé",
    portalName: "BuyDirect",
    portalSuffix: "Powered by SAP Ariba",
    logoLetter: "N",
    primary: "#0F5132",
    accent: "#3AA675",
    userEmail: "brand@nestle.example",
    userInitials: "RM",
    userName: "Rohan Mehta",
    userRole: "Brand · Nestlé India",
    spendYtd: "₹24.6 Cr",
    openReqs: "18",
    approved: "62",
    pending: "9",
    catalog: [
      { icon: Package, label: "Food Ingredients", vendor: "Cargill · Olam · Bunge", price: "Contract" },
      { icon: Truck, label: "Cold Chain & Logistics", vendor: "Snowman · TCI Cold Chain", price: "RFQ" },
      { icon: Users, label: "Trade Marketing Agencies", vendor: "Ogilvy Activation · Geometry", price: "MSA" },
      { icon: FileText, label: "Creative Agencies", vendor: "McCann · Ogilvy · Publicis", price: "MSA" },
      { icon: LineChart, label: "Retail Merchandising", vendor: "SmartCircle · Trigger", price: "Retainer" },
      { icon: Zap, label: "Data & Analytics", vendor: "Nielsen · Kantar · Mu Sigma", price: "Subscription" },
    ],
  },
  cocacola: {
    key: "cocacola",
    displayName: "Coca-Cola",
    portalName: "Supplier Marketplace",
    portalSuffix: "Powered by Coupa",
    logoLetter: "C",
    primary: "#F40009",
    accent: "#000000",
    userEmail: "marketing@cocacola.example",
    userInitials: "KN",
    userName: "Kavya Nair",
    userRole: "Marketing · Coca-Cola India",
    spendYtd: "₹32.1 Cr",
    openReqs: "22",
    approved: "71",
    pending: "6",
    catalog: [
      { icon: Package, label: "Bottling & Packaging", vendor: "AGI · Manjushree · Berger", price: "Contract" },
      { icon: Truck, label: "Fleet & Distribution", vendor: "Ashok Leyland · TATA Motors", price: "Framework" },
      { icon: Users, label: "Sponsorships & Events", vendor: "IMG · Wizcraft · Encompass", price: "SOW" },
      { icon: FileText, label: "Creative Agencies", vendor: "Ogilvy · Wieden+Kennedy · BBH", price: "MSA" },
      { icon: LineChart, label: "Media Buying", vendor: "GroupM · Publicis Media", price: "Retainer" },
      { icon: Zap, label: "Consumer Insights", vendor: "Kantar · Ipsos · NielsenIQ", price: "Subscription" },
    ],
  },
};

const NAV_ITEMS = [
  { icon: Home, label: "Home", active: true },
  { icon: ClipboardList, label: "Requisitions" },
  { icon: FileText, label: "Purchase Orders" },
  { icon: Receipt, label: "Invoices" },
  { icon: Wallet, label: "Expenses" },
  { icon: LineChart, label: "Analytics" },
];

export default function OneBuyDemo() {
  const { tenant: tenantParam } = useParams();
  const cfg = TENANTS[(tenantParam || "unilever").toLowerCase()] || TENANTS.unilever;
  const { login } = useAuth();
  const nav = useNavigate();
  const [launching, setLaunching] = useState(false);

  const punchOutToEngage360 = async () => {
    if (launching) return;
    setLaunching(true);
    toast.info(`Punching out from ${cfg.displayName} ${cfg.portalName} → Engage360…`, { duration: 2500 });
    try {
      // Record the punch-out source so the app shell can render a
      // "← Return to <portal>" chip during the impersonated session.
      try {
        sessionStorage.setItem(
          "propay_punchout_source",
          JSON.stringify({
            key: cfg.key,
            displayName: cfg.displayName,
            portalName: cfg.portalName,
            returnPath: `/onebuy${cfg.key === "unilever" ? "" : "/" + cfg.key}`,
            primary: cfg.primary,
            enteredAt: Date.now(),
          })
        );
      } catch (err) {
        console.warn("PunchOut context not stored (storage disabled):", err);
      }
      const user = await login(cfg.userEmail, "Propay@2026");
      toast.success(`Welcome from ${cfg.displayName} ${cfg.portalName}, ${user.name}`);
      setTimeout(() => nav(homeForRole(user.role)), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PunchOut failed");
      setLaunching(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="onebuy-demo">
      {/* Portal top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="px-6 h-14 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded grid place-items-center text-white font-bold text-sm"
              style={{ background: cfg.primary }}
            >
              {cfg.logoLetter}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 leading-none">{cfg.displayName}</div>
              <div className="font-heading font-black text-slate-900 leading-none mt-0.5">{cfg.portalName}</div>
            </div>
            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">{cfg.portalSuffix}</span>
          </div>
          <div className="flex-1 max-w-2xl mx-6 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Search catalogue, vendors, requisitions…"
              className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none text-sm"
              style={{ borderColor: cfg.primary + "20" }}
            />
          </div>
          <div className="flex items-center gap-3 text-slate-500">
            <button className="relative hover:text-slate-900" title="Cart">
              <ShoppingCart size={18} />
              <span className="absolute -top-1 -right-1 w-4 h-4 grid place-items-center bg-rose-500 text-white text-[10px] rounded-full font-bold">3</span>
            </button>
            <button className="hover:text-slate-900" title="Notifications"><Bell size={18} /></button>
            <button className="hover:text-slate-900" title="Help"><HelpCircle size={18} /></button>
            <div className="flex items-center gap-2 border-l border-slate-200 pl-3 ml-1">
              <div className="w-7 h-7 rounded-full text-white grid place-items-center text-[11px] font-bold" style={{ background: cfg.primary }}>{cfg.userInitials}</div>
              <div className="text-xs">
                <div className="font-semibold text-slate-800 leading-tight">{cfg.userName}</div>
                <div className="text-slate-500">{cfg.userRole}</div>
              </div>
              <ChevronDown size={14} className="text-slate-400" />
            </div>
          </div>
        </div>
        <div className="px-6 h-10 flex items-center gap-1 bg-slate-50 border-t border-slate-100 text-sm overflow-x-auto">
          {NAV_ITEMS.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.label}
                className={`inline-flex items-center gap-1.5 px-3 h-full font-semibold ${n.active ? "border-b-2" : "text-slate-600 hover:text-slate-900"}`}
                style={n.active ? { color: cfg.primary, borderColor: cfg.primary } : {}}
              >
                <Icon size={14} />
                {n.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-12 gap-6">
        {/* Catalogue */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Welcome, {cfg.userName.split(" ")[0]}</div>
            <h1 className="font-heading text-3xl font-black text-slate-900 mt-1">Approved Vendor Catalogue</h1>
            <p className="text-sm text-slate-600 mt-1">Buy from any approved vendor — every purchase is auto-routed through your cost centre and finance approvals.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: "Open Requisitions", v: cfg.openReqs, c: "text-slate-800" },
              { l: "Approved this month", v: cfg.approved, c: "text-emerald-600" },
              { l: "Pending review", v: cfg.pending, c: "text-amber-600" },
              { l: "Spend YTD", v: cfg.spendYtd, c: "" },
            ].map((s) => (
              <div key={s.l} className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.l}</div>
                <div className={`font-heading font-black text-2xl mt-0.5 ${s.c}`} style={!s.c ? { color: cfg.primary } : {}}>{s.v}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Browse categories</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cfg.catalog.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="bg-white rounded-lg border border-slate-200 hover:shadow-sm p-4 transition-all cursor-not-allowed">
                    <Icon size={22} style={{ color: cfg.primary }} />
                    <div className="font-semibold text-slate-900 mt-2">{c.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.vendor}</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-2">{c.price}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 italic border-t border-slate-200 pt-3">
            This is a demo screen simulating {cfg.displayName} {cfg.portalName} for the purpose of showcasing the Engage360 PunchOut integration. Live portal content is not shown.
          </div>
        </div>

        {/* PunchOut card */}
        <div className="col-span-12 lg:col-span-4">
          <div className="sticky top-32 space-y-4">
            <div className="rounded-xl border-2 shadow-lg overflow-hidden" style={{ borderColor: cfg.accent }} data-testid="engage360-punchout-card">
              <div className="p-4" style={{ background: `linear-gradient(135deg, ${cfg.primary} 0%, ${cfg.accent} 100%)` }}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">
                  PunchOut · {cfg.portalName} Integration
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Sparkles size={20} />
                  <div className="font-heading font-black text-xl leading-tight">Access Engage360</div>
                </div>
                <div className="text-white/90 text-sm mt-1">Influencer Management</div>
              </div>
              <div className="p-4 bg-white space-y-3">
                <p className="text-sm text-slate-700">
                  Launch <b>Engage360</b> to submit influencer engagements, approve deliverables and authorise payments.
                  Sign-in is handled automatically via {cfg.portalName}.
                </p>
                <ul className="text-xs text-slate-600 space-y-1.5">
                  <li className="flex items-start gap-2"><ShieldCheck size={12} className="text-emerald-600 mt-0.5" /> One-vendor model — ProPay is your only creator payee.</li>
                  <li className="flex items-start gap-2"><Users size={12} className="text-emerald-600 mt-0.5" /> KYS-verified creators only, audited from onboarding to payment.</li>
                  <li className="flex items-start gap-2"><Zap size={12} className="text-emerald-600 mt-0.5" /> AI-enabled review flags every deliverable in seconds.</li>
                </ul>
                <button
                  onClick={punchOutToEngage360}
                  disabled={launching}
                  data-testid="engage360-punchout-btn"
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ background: cfg.primary }}
                >
                  {launching ? "Launching Engage360…" : (<>Launch Engage360 <ArrowUpRight size={16} /></>)}
                </button>
                <div className="text-[10px] text-slate-500 text-center">
                  Session will be created as <b>{cfg.userName}</b> ({cfg.userRole}).
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">About this integration</div>
              <div className="text-xs text-slate-600 space-y-2">
                <p>
                  Engage360 is available to {cfg.displayName} India via a {cfg.portalSuffix.replace("Powered by ", "")} PunchOut catalogue.
                  A signed session token is exchanged behind the scenes so you never have to sign in twice.
                </p>
                <p className="text-slate-500 italic">
                  Vendor: <b className="not-italic">PROPAY GLOBAL PRIVATE LIMITED</b> · Contract: <b className="not-italic">MSA-{cfg.logoLetter}L-2026-04</b>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
